import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
  computed,
  effect,
  DestroyRef,
  OnInit,
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatBottomSheet,
  MatBottomSheetModule,
  MatBottomSheetRef,
  MAT_BOTTOM_SHEET_DATA,
} from '@angular/material/bottom-sheet';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { ActiveBranchService } from '../../services/branch/active-branch.service';
import { BranchService } from '../../services/supabase/branch.service';
import { ErrorLoggerService } from '../../services/error/error-logger.service';
import { AuthService } from '../../services/auth/auth.service';
import { Branch } from '../../models';
import { HasPermissionDirective } from '../../directives/has-permission.directive';

@Component({
  selector: 'app-branch-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatMenuModule, HasPermissionDirective],
  templateUrl: './branch-picker.component.html',
  styleUrl: './branch-picker.component.scss',
})
export class BranchPickerComponent implements OnInit {
  readonly active = inject(ActiveBranchService);
  private readonly branchService = inject(BranchService);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly breakpoints = inject(BreakpointObserver);
  private readonly errorLogger = inject(ErrorLoggerService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  readonly hideOnDesktop = input<boolean>(false);

  readonly branches = signal<Branch[]>([]);

  readonly isMobile = toSignal(
    this.breakpoints.observe('(max-width: 899px)').pipe(map(s => s.matches)),
    { initialValue: false },
  );

  readonly activeBranchName = computed<string | null>(() => {
    const id = this.active.activeBranchId();
    if (!id) return null;
    const found = this.branches().find(b => b.id === id);
    return found?.name ?? null;
  });

  readonly hostHidden = computed(() => {
    const hide = this.hideOnDesktop() && !this.isMobile();
    return hide;
  });

  constructor() {
    // Reload branches when the active practice changes.
    effect(() => {
      this.auth.practiceId();
      void this.loadBranches();
    });
  }

  ngOnInit(): void {
    void this.loadBranches();
  }

  private async loadBranches(): Promise<void> {
    if (!this.auth.practiceId()) return;
    try {
      const list = await this.branchService.fetchActive();
      this.branches.set(list);
    } catch (error) {
      this.errorLogger.log(error, undefined, { context: 'BranchPickerComponent.loadBranches' });
    }
  }

  onMobileTap(): void {
    if (!this.isMobile()) return;
    const ref = this.bottomSheet.open(BranchPickerSheetComponent, {
      data: {
        branches: this.branches(),
        activeId: this.active.activeBranchId(),
      },
    });
    ref
      .afterDismissed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result === undefined) return;
        this.active.setActive(result);
      });
  }

  selectBranch(branchId: string | null): void {
    this.active.setActive(branchId);
  }

  trackByBranchId(_: number, b: Branch): string {
    return b.id;
  }
}

@Component({
  selector: 'app-branch-picker-sheet',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatBottomSheetModule, MatIconModule],
  template: `
    <div class="sheet">
      <h3 class="sheet__title">Select branch</h3>
      <button class="sheet__option" (click)="pick(null)">
        @if (data.activeId === null) {
          <mat-icon>check</mat-icon>
        }
        <span [class.sheet__option--ghost]="data.activeId !== null">All branches</span>
      </button>
      @for (b of data.branches; track b) {
        <button
          class="sheet__option"
          (click)="pick(b.id)"
          >
          @if (data.activeId === b.id) {
            <mat-icon>check</mat-icon>
          }
          <span [class.sheet__option--ghost]="data.activeId !== b.id">{{ b.name }}</span>
        </button>
      }
    </div>
    `,
  styles: [`
    .sheet { padding: var(--space-md); background: var(--surface); color: var(--text-primary); }
    .sheet__title { margin: 0 0 var(--space-md); font-size: 1rem; font-weight: 600; color: var(--text-primary); }
    .sheet__option {
      display: flex; align-items: center; gap: var(--space-sm);
      padding: var(--space-sm); width: 100%; text-align: left;
      background: transparent; border: none; cursor: pointer;
      border-radius: var(--radius-md);
      color: var(--text-primary);
      font: inherit;
    }
    .sheet__option:hover { background-color: var(--surface-variant); }
    .sheet__option--ghost { padding-left: 24px; }
  `],
})
export class BranchPickerSheetComponent {
  private readonly ref = inject(MatBottomSheetRef<BranchPickerSheetComponent, string | null>);
  readonly data = inject<{ branches: Branch[]; activeId: string | null }>(
    MAT_BOTTOM_SHEET_DATA,
  );

  pick(id: string | null): void {
    this.ref.dismiss(id);
  }
}
